import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  FlaskConical,
  Plus,
  Trash2,
  Loader2,
  Package,
} from 'lucide-react';

export const RecipeModal = ({ open, onOpenChange, menuItem }) => {
  const { api } = useAuth();
  const { currentOutlet } = useOutlet();
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    if (open && menuItem && currentOutlet) {
      fetchInventory();
      fetchRecipes();
    }
  }, [open, menuItem, currentOutlet]);

  const fetchInventory = async () => {
    try {
      const resp = await api.get('/inventory', { params: { outlet_id: currentOutlet.id } });
      setInventory(resp.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
    }
  };

  const fetchRecipes = async () => {
    if (!menuItem?.id) return;
    try {
      const resp = await api.get(`/recipes/${menuItem.id}`);
      setRecipes(resp.data);
      
      // Load ingredients for base recipe (no variant)
      const baseRecipe = resp.data.find(r => !r.variant_id);
      if (baseRecipe) {
        setIngredients(baseRecipe.ingredients || []);
      } else {
        setIngredients([]);
      }
    } catch (error) {
      console.error('Failed to fetch recipes:', error);
    }
  };

  const handleVariantChange = (variantId) => {
    setSelectedVariant(variantId);
    const recipe = recipes.find(r => r.variant_id === (variantId === 'base' ? null : variantId));
    setIngredients(recipe?.ingredients || []);
  };

  const addIngredient = () => {
    setIngredients([
      ...ingredients,
      { inventory_item_id: '', inventory_item_name: '', quantity: 0, unit: '' },
    ]);
  };

  const updateIngredient = (index, field, value) => {
    setIngredients(prev => prev.map((ing, idx) => {
      if (idx === index) {
        if (field === 'inventory_item_id') {
          const item = inventory.find(i => i.id === value);
          return {
            ...ing,
            inventory_item_id: value,
            inventory_item_name: item?.name || '',
            unit: item?.unit || '',
          };
        }
        return { ...ing, [field]: field === 'quantity' ? parseFloat(value) || 0 : value };
      }
      return ing;
    }));
  };

  const removeIngredient = (index) => {
    setIngredients(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.post('/recipes', {
        menu_item_id: menuItem.id,
        variant_id: selectedVariant === 'base' ? null : selectedVariant,
        ingredients: ingredients.filter(i => i.inventory_item_id && i.quantity > 0),
      });
      toast.success('Recipe saved successfully!');
      fetchRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipe');
    } finally {
      setLoading(false);
    }
  };

  if (!menuItem) return null;

  const hasVariants = menuItem.variants?.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Recipe - {menuItem.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Variant Selection */}
          {hasVariants && (
            <div>
              <Label>Recipe for Variant</Label>
              <Select
                value={selectedVariant || 'base'}
                onValueChange={handleVariantChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select variant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">Base Recipe (All Variants)</SelectItem>
                  {menuItem.variants.map((variant) => (
                    <SelectItem key={variant.id} value={variant.id}>
                      {variant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Set a base recipe for all variants, or create specific recipes for each.
              </p>
            </div>
          )}

          {/* Existing Recipes Info */}
          {recipes.length > 0 && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Existing Recipes:</p>
              <div className="flex flex-wrap gap-2">
                {recipes.map((recipe) => (
                  <Badge key={recipe.id} variant="outline">
                    {recipe.variant_id 
                      ? menuItem.variants?.find(v => v.id === recipe.variant_id)?.name 
                      : 'Base Recipe'
                    }
                    ({recipe.ingredients?.length || 0} ingredients)
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Ingredients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Ingredients (per 1 unit sold)</Label>
              <Button type="button" variant="outline" size="sm" onClick={addIngredient}>
                <Plus className="h-3 w-3 mr-1" />
                Add Ingredient
              </Button>
            </div>

            {ingredients.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No ingredients defined</p>
                <p className="text-xs text-muted-foreground">Add ingredients to enable stock auto-deduction</p>
              </div>
            ) : (
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {ingredients.map((ing, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg">
                      <Select
                        value={ing.inventory_item_id}
                        onValueChange={(val) => updateIngredient(idx, 'inventory_item_id', val)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select ingredient" />
                        </SelectTrigger>
                        <SelectContent>
                          {inventory.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name} ({item.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        value={ing.quantity || ''}
                        onChange={(e) => updateIngredient(idx, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground w-12">
                        {ing.unit}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeIngredient(idx)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
            <p className="font-medium text-blue-600 dark:text-blue-400">How stock deduction works:</p>
            <ul className="mt-1 space-y-1 text-muted-foreground">
              <li>• <strong>Prepaid orders:</strong> Stock deducted when order is confirmed</li>
              <li>• <strong>Postpaid orders:</strong> Stock deducted when payment is complete</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Recipe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
